import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid } from "@mui/material";
import { ChannelActions } from "@Store";
import { useDispatch } from "react-redux";
import { IChannel } from "@Interfaces";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { TbPrimaryButton } from "@Styled/Layout";
import { NotificationActions } from "@Store";
import GetImagePath from "@Hooks/getImagePath";
import { useStyles } from "./ChannelSettingsDialog.styles";
import useRestrictions from "@Hooks/useRestrictions";
import { hasEntitlement } from "@Utils/accountEntitlements";
import { useTranslation } from "react-i18next";
import i18n from "i18next"; // Allow translating the static dialog title at import time.
import {
    BrandingSection,
    CompletionRedirectSection,
    CustomTextFieldSection,
    CustomVariablesSection,
    DemographicsSection,
    GeneralSettingsSection,
    VotingRulesSection,
} from "./ChannelSettingsDialog.sections";
import {
    ChannelSettingsFormData,
    MAX_CUSTOM_VARIABLES,
} from "./ChannelSettingsDialog.types";
import { resolveRedirectTarget } from "@Utils/redirectUrl";
import {
    createReservedDemographicCustomVariableKeys,
    normalizeCustomVariableLabel,
} from "./ChannelSettingsDialog.helpers";

// Renders the settings form for a single channel, wiring defaults and handlers.
const ChannelSettingsContent: React.FC<{ channel: IChannel }> = ({
    channel,
}) => {
    const { t } = useTranslation();
    const [accountType] = useRestrictions();
    // A saved redirect without a usable URL (e.g. blank, set through the API) shows as off.
    const savedRedirectUrl =
        resolveRedirectTarget(channel.settings?.completion_redirect_url) ?? "";
    const savedRedirectEnabled =
        Boolean(channel.settings?.completion_redirect_enabled) &&
        savedRedirectUrl !== "";
    const canUseAdvancedDataCollection = hasEntitlement(
        accountType,
        "can_use_advanced_data_collection"
    );
    const canCustomizeBranding = hasEntitlement(
        accountType,
        "can_customize_branding"
    );
    const isAdvancedDataCollectionLocked = !canUseAdvancedDataCollection;
    const isBrandingLocked = !canCustomizeBranding;

    const classes = useStyles(isBrandingLocked);
    const dispatch = useDispatch<any>();
    const isSubChannel = Boolean(channel.parent_channel_id);

    const [isEmailRequiredChecked, setIsEmailRequiredChecked] = useState(
        channel.settings?.is_email_required ?? false
    );
    const [checked, setChecked] = useState(
        channel.settings?.hide_results_before_voting ?? false
    );

    type ChannelDemographicFields = IChannel["settings"] extends {
        demographic_fields?: infer D;
    }
        ? D
        : never;
    const demographicFields = channel.settings?.demographic_fields as
        | (ChannelDemographicFields & {
              country?: boolean;
              nationality?: boolean;
          })
        | undefined;
    const hasActiveDemographics =
        !!demographicFields &&
        Object.values(demographicFields).some((value) => !!value);
    const availableDemographicFields =
        channel.settings?.available_demographic_fields ?? [];
    const reservedDemographicCustomVariableKeys = useMemo(
        () =>
            createReservedDemographicCustomVariableKeys(
                availableDemographicFields,
                t
            ),
        [availableDemographicFields, t]
    );

    const validateCustomVariableLabel = (value?: string) => {
        const normalizedValue = normalizeCustomVariableLabel(
            value?.trim() ?? ""
        );

        if (!normalizedValue) {
            return true;
        }

        return (
            !reservedDemographicCustomVariableKeys.has(normalizedValue) ||
            t(
                "dialogs.channel_settings.custom_variable_reserved_demographic_error"
            )
        );
    };

    const validateUniqueCustomVariableLabel = (
        value: string | undefined,
        index: number
    ) => {
        const normalizedValue = normalizeCustomVariableLabel(
            value?.trim() ?? ""
        );

        if (!normalizedValue) {
            return true;
        }

        const duplicateExists = (watchedCustomVariables || []).some(
            (variable, variableIndex) =>
                variableIndex !== index &&
                normalizeCustomVariableLabel(variable?.label?.trim() ?? "") ===
                    normalizedValue
        );

        return (
            !duplicateExists ||
            t("dialogs.channel_settings.custom_variable_duplicate_error")
        );
    };

    const existingCustomVariables = channel.settings?.custom_variables ?? [];
    const defaultCustomVariables = existingCustomVariables
        .slice(0, MAX_CUSTOM_VARIABLES)
        .map((variable) => ({
            label: variable?.label || "",
            options: (variable?.custom_options || [])
                .filter((option) => option)
                .join(", "),
        }));
    const hasActiveCustomVariables = defaultCustomVariables.length > 0;

    const [demographicsToggled, setDemographicsToggled] = useState<boolean>(
        hasActiveDemographics
    );

    const [customVariablesToggled, setCustomVariablesToggled] =
        useState<boolean>(hasActiveCustomVariables);
    const skipNextCustomVariableAutoAppend = useRef(false);
    const [customTextFieldToggled, setCustomTextFieldToggled] = useState(
        channel.settings?.custom_text_field_enabled ?? false
    );

    const [qrColor, setQrColor] = useState<string>(
        channel.settings?.qr_code_color as string
    );

    const profileImgPath = GetImagePath(
        channel?.profile_image?.public_url,
        channel?.profile_image?.fid
    );

    const [
        useChannelProfileAsQrLogoChecked,
        setUseChannelProfileAsQrLogoChecked,
    ] = useState<boolean | undefined>(
        channel.settings?.use_channel_profile_as_qr_logo ?? false
    );
    const [completionRedirectToggled, setCompletionRedirectToggled] =
        useState<boolean>(savedRedirectEnabled);
    const [allowDoubleVoteChecked, setAllowDoubleVoteChecked] =
        useState<boolean>(channel.settings?.allow_double_vote ?? false);
    const [allowCommentsChecked, setAllowCommentsChecked] = useState<boolean>(
        channel.settings?.allow_comments ?? true
    );
    const [displayProfilePictureChecked, setDisplayProfilePictureChecked] =
        useState<boolean>(channel.settings?.display_profile_picture ?? true);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        control,
        setValue,
    } = useForm<ChannelSettingsFormData>({
        defaultValues: {
            title: channel.title,
            description: channel.description,
            qrColor: channel.settings?.qr_code_color,
            votersVp: channel.settings?.voters_vp ?? "",
            ctaMsg: channel.settings?.cta_msg ?? "",
            // Ensure RHF has an initial value for the email requirement toggle.
            isEmailRequired: channel.settings?.is_email_required ?? false,
            // Persist master demographic toggle in the form state as well.
            isDemographic: hasActiveDemographics,
            // Default for QR logo usage toggle
            useChannelProfileAsQrLogo:
                channel.settings?.use_channel_profile_as_qr_logo ?? false,
            // Keep RHF in sync with local setting states
            hideResults: channel.settings?.hide_results_before_voting ?? false,
            timer: channel.settings?.voting_time_limit ?? 0,
            allowDoubleVote: channel.settings?.allow_double_vote ?? false,
            allowComments: channel.settings?.allow_comments ?? true,
            displayProfilePicture:
                channel.settings?.display_profile_picture ?? true,
            doubleVoteCooldownMinutes: Math.max(
                1,
                channel.settings?.double_vote_cooldown_minutes ?? 1
            ),
            collectProfession:
                channel.settings?.demographic_fields?.profession ?? false,
            collectSector:
                channel.settings?.demographic_fields?.sector ?? false,
            collectIndustry:
                channel.settings?.demographic_fields?.industry ?? false,
            collectRole: channel.settings?.demographic_fields?.role ?? false,
            collectOrganizationType:
                channel.settings?.demographic_fields?.organization_type ??
                false,
            collectCountry:
                channel.settings?.demographic_fields?.country ?? false,
            collectNationality:
                channel.settings?.demographic_fields?.nationality ?? false,
            collectAge: channel.settings?.demographic_fields?.age ?? false,
            collectGender:
                channel.settings?.demographic_fields?.gender ?? false,
            customVariables:
                defaultCustomVariables.length > 0
                    ? defaultCustomVariables
                    : [{ label: "", options: "" }],
            customTextFieldEnabled:
                channel.settings?.custom_text_field_enabled ?? false,
            customTextFieldQuestion:
                channel.settings?.custom_text_field_question ?? "",
            completionRedirectEnabled: savedRedirectEnabled,
            completionRedirectUrl: savedRedirectUrl,
        },
    });
    const {
        fields: customVariableFields,
        append: appendCustomVariable,
        remove: removeCustomVariable,
    } = useFieldArray({
        control,
        name: "customVariables",
    });

    const watchDoubleVoteCooldown = watch("doubleVoteCooldownMinutes");
    const watchedCustomVariables = useWatch({
        control,
        name: "customVariables",
    });
    const watchCustomTextFieldQuestion = watch("customTextFieldQuestion");
    // Toggle whether guest email collection is required and keep RHF in sync.
    const handleChangeEmailRequired = () => {
        const next = !isEmailRequiredChecked;
        setIsEmailRequiredChecked(next);
        setValue("isEmailRequired", next, { shouldDirty: true });
    };

    // Enable/disable demographic switches as a group.
    const handleDemographicsToggled = () => {
        setDemographicsToggled((prev) => {
            const next = !prev;
            setValue("isDemographic", next, { shouldDirty: true });
            return next;
        });
    };

    // Toggle use of the channel profile image on the QR code.
    const handleChangeQRLogo = () => {
        const next = !useChannelProfileAsQrLogoChecked;
        setUseChannelProfileAsQrLogoChecked(next);
        setValue("useChannelProfileAsQrLogo", next, { shouldDirty: true });
    };

    // Toggle hiding poll results before voting.
    const handleChange = () => {
        const next = !checked;
        setChecked(next);
        setValue("hideResults", next, { shouldDirty: true });
    };

    const handleAllowDoubleVoteToggle = () => {
        const next = !allowDoubleVoteChecked;
        setAllowDoubleVoteChecked(next);
        setValue("allowDoubleVote", next, { shouldDirty: true });
        if (!next) {
            setValue("doubleVoteCooldownMinutes", 1, { shouldDirty: true });
        } else if (
            !watchDoubleVoteCooldown ||
            Number(watchDoubleVoteCooldown) < 1
        ) {
            setValue("doubleVoteCooldownMinutes", 1, { shouldDirty: true });
        }
    };

    const handleAllowCommentsToggle = () => {
        const next = !allowCommentsChecked;
        setAllowCommentsChecked(next);
        setValue("allowComments", next, { shouldDirty: true });
    };

    const handleDisplayProfilePictureToggle = () => {
        const next = !displayProfilePictureChecked;
        setDisplayProfilePictureChecked(next);
        setValue("displayProfilePicture", next, { shouldDirty: true });
    };

    // Persist the selected color to the form and local state.
    const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const color = event.target.value;
        setValue("qrColor", color, { shouldDirty: true });
        setQrColor(color);
    };

    // Toggle custom-variable collection and reset fields when disabled.
    const handleCustomVariablesToggle = () => {
        setCustomVariablesToggled((prev) => {
            const next = !prev;
            if (!next) {
                for (
                    let index = customVariableFields.length - 1;
                    index >= 0;
                    index -= 1
                ) {
                    removeCustomVariable(index);
                }
                setValue("customVariables", [], { shouldDirty: true });
            } else if (customVariableFields.length === 0) {
                appendCustomVariable({ label: "", options: "" });
            }
            return next;
        });
    };

    const handleCustomTextFieldToggle = () => {
        setCustomTextFieldToggled((prev) => {
            const next = !prev;
            setValue("customTextFieldEnabled", next, { shouldDirty: true });
            if (!next) {
                setValue("customTextFieldQuestion", "", {
                    shouldDirty: true,
                });
            }
            return next;
        });
    };

    // Keep the URL when switching off so re-enabling restores it.
    const handleCompletionRedirectToggle = () => {
        const next = !completionRedirectToggled;
        setCompletionRedirectToggled(next);
        setValue("completionRedirectEnabled", next, { shouldDirty: true });
    };

    const isInitialDemographicsSync = useRef(true);

    // Keep individual demographic switches in sync with the master toggle.
    useEffect(() => {
        if (isInitialDemographicsSync.current) {
            isInitialDemographicsSync.current = false;
            return;
        }

        // Keep current per-field selections when enabling demographics.
        // Only clear all fields when disabling the master toggle.
        if (!demographicsToggled) {
            setValue("collectProfession", false, { shouldDirty: true });
            setValue("collectSector", false, { shouldDirty: true });
            setValue("collectIndustry", false, { shouldDirty: true });
            setValue("collectRole", false, { shouldDirty: true });
            setValue("collectOrganizationType", false, { shouldDirty: true });
            setValue("collectCountry", false, { shouldDirty: true });
            setValue("collectNationality", false, { shouldDirty: true });
            setValue("collectAge", false, { shouldDirty: true });
            setValue("collectGender", false, { shouldDirty: true });
        }
    }, [demographicsToggled, setValue]);

    // Handles removing a custom variable row or clearing its contents.
    const handleRemoveCustomVariable = (index: number) => {
        const currentRow =
            watchedCustomVariables?.[index] ?? customVariableFields[index];
        const labelValue = (currentRow?.label ?? "").trim();
        const optionsCount = (currentRow?.options ?? "")
            .split(",")
            .map((option) => option.trim())
            .filter((option) => option).length;

        // Ignore remove clicks for empty secondary rows to avoid flicker/recreate confusion.
        if (index > 0 && !labelValue && optionsCount === 0) {
            return;
        }

        if (customVariableFields.length <= 1) {
            setValue(`customVariables.${index}.label`, "", {
                shouldDirty: true,
            });
            setValue(`customVariables.${index}.options`, "", {
                shouldDirty: true,
            });
            return;
        }
        skipNextCustomVariableAutoAppend.current = true;
        removeCustomVariable(index);
    };

    const sanitizeLineBreaks = useMemo(
        () => (value: string) => value.replace(/\s*\n\s*/g, " "),
        []
    );

    const normalizeOptions = useMemo(() => {
        return (raw?: string | string[]) => {
            if (Array.isArray(raw)) {
                return raw
                    .map((chip) => sanitizeLineBreaks(chip.trim()))
                    .filter((chip) => chip);
            }
            return (raw || "")
                .split(",")
                .map((chip) => sanitizeLineBreaks(chip.trim()))
                .filter((chip) => chip);
        };
    }, [sanitizeLineBreaks]);

    const denormalizeOptions = useMemo(
        () => (chips: string[]) => chips.join(", "),
        []
    );

    // Defensive cap: never allow more rows than the configured maximum.
    useEffect(() => {
        if (customVariableFields.length <= MAX_CUSTOM_VARIABLES) {
            return;
        }

        const overflowIndexes = Array.from(
            { length: customVariableFields.length - MAX_CUSTOM_VARIABLES },
            (_, offset) => MAX_CUSTOM_VARIABLES + offset
        );

        removeCustomVariable(overflowIndexes);
    }, [customVariableFields.length, removeCustomVariable]);

    // Auto-append an empty custom variable field when the last one is filled.
    useEffect(() => {
        if (skipNextCustomVariableAutoAppend.current) {
            skipNextCustomVariableAutoAppend.current = false;
            return;
        }

        if (isAdvancedDataCollectionLocked) {
            return;
        }

        if (!customVariablesToggled) {
            return;
        }

        if (customVariableFields.length === 0) {
            return;
        }

        const values = (watchedCustomVariables || []).slice(
            0,
            customVariableFields.length
        ) as Array<{ label?: string; options?: string }>;

        const hasIncomplete = values.some(
            (item) =>
                !item ||
                !item.label ||
                !item.label.trim() ||
                !item.options ||
                normalizeOptions(item.options).length === 0
        );

        if (
            !hasIncomplete &&
            customVariableFields.length < MAX_CUSTOM_VARIABLES
        ) {
            appendCustomVariable({ label: "", options: "" });
        }
    }, [
        appendCustomVariable,
        customVariableFields.length,
        customVariablesToggled,
        isAdvancedDataCollectionLocked,
        watchedCustomVariables,
        normalizeOptions,
    ]);

    // Submit the form and dispatch the channel update.
    const onSubmit = async (data: ChannelSettingsFormData) => {
        if (isSubChannel) {
            const payload = {
                title: data.title,
                description: data.description,
            };

            try {
                dispatch(ChannelActions.updateChannel(channel, payload));
            } catch (error) {
                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage: t("snackbar_fail"),
                    })
                );
            } finally {
                dispatch(dialogActions.hideDialog());
            }
            return;
        }

        const sanitizedCustomVariables = customVariablesToggled
            ? (data.customVariables || [])
                  .slice(0, MAX_CUSTOM_VARIABLES)
                  .map((variable) => {
                      const label = sanitizeLineBreaks(
                          variable.label ?? ""
                      ).trim();
                      const options = variable.options
                          ?.split(",")
                          .map((option) => sanitizeLineBreaks(option).trim())
                          .filter((option) => option);
                      if (!label || !options || options.length === 0) {
                          return null;
                      }
                      return {
                          label,
                          custom_options: options,
                      } as { label: string; custom_options: string[] };
                  })
                  .filter(
                      (
                          item
                      ): item is { label: string; custom_options: string[] } =>
                          item !== null
                  )
            : [];

        const sanitizedCustomTextFieldQuestion = sanitizeLineBreaks(
            data.customTextFieldQuestion ?? ""
        ).trim();
        const customTextFieldQuestionValue = data.customTextFieldEnabled
            ? sanitizedCustomTextFieldQuestion ||
              channel.settings?.custom_text_field_question ||
              ""
            : "";

        const cooldownMinutes = Math.max(
            1,
            Number(data.doubleVoteCooldownMinutes) || 0
        );

        const updatedSettings: Record<string, any> = {
            hide_results_before_voting: data.hideResults,
            voting_time_limit: data.timer,
            qr_code_color: data.qrColor,
            cta_msg: data.ctaMsg ?? "",
            voters_vp: data.votersVp ?? "",
            is_email_required: data.isEmailRequired,
            demographic_fields: {
                profession: data.collectProfession,
                sector: data.collectSector,
                industry: data.collectIndustry,
                role: data.collectRole,
                organization_type: data.collectOrganizationType,
                country: data.collectCountry,
                nationality: data.collectNationality,
                age: data.collectAge,
                gender: data.collectGender,
            },
            custom_variables: sanitizedCustomVariables,
            custom_text_field_enabled: data.customTextFieldEnabled,
            custom_text_field_question: customTextFieldQuestionValue,
            is_demographics_required: data.isDemographic,
            use_channel_profile_as_qr_logo: data.useChannelProfileAsQrLogo,
            allow_double_vote: data.allowDoubleVote,
            allow_comments: data.allowComments,
            display_profile_picture: data.displayProfilePicture,
            double_vote_cooldown_minutes: data.allowDoubleVote
                ? cooldownMinutes
                : 0,
            completion_redirect_enabled: data.completionRedirectEnabled,
            // A leftover invalid URL (toggle off) becomes "" so the API accepts the save.
            completion_redirect_url:
                resolveRedirectTarget(data.completionRedirectUrl) ?? "",
        };

        if (isAdvancedDataCollectionLocked) {
            delete updatedSettings.demographic_fields;
            delete updatedSettings.custom_variables;
            delete updatedSettings.custom_text_field_enabled;
            delete updatedSettings.custom_text_field_question;
            delete updatedSettings.is_demographics_required;
            delete updatedSettings.allow_double_vote;
            delete updatedSettings.double_vote_cooldown_minutes;
        }

        if (isBrandingLocked) {
            delete updatedSettings.qr_code_color;
            delete updatedSettings.use_channel_profile_as_qr_logo;
            delete updatedSettings.completion_redirect_enabled;
            delete updatedSettings.completion_redirect_url;
        }

        const updatedData = {
            title: data.title,
            description: data.description,
            settings: updatedSettings,
        };

        try {
            dispatch(ChannelActions.updateChannel(channel, updatedData));
        } catch (error) {
            dispatch(
                NotificationActions.notification({
                    snackbarOpen: true,
                    snackbarType: "error",
                    snackbarMessage: t("snackbar_fail"),
                })
            );
        } finally {
            dispatch(dialogActions.hideDialog());
        }
    };

    return (
        <>
            <Box sx={{ mt: 2 }}>
                <Box sx={{ mt: 2 }}>
                    <form
                        id="channel-dialog"
                        noValidate
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Grid sx={classes.root} container spacing={3}>
                            <GeneralSettingsSection
                                allowCommentsChecked={allowCommentsChecked}
                                control={control}
                                displayProfilePictureChecked={
                                    displayProfilePictureChecked
                                }
                                errors={errors}
                                handleAllowCommentsToggle={
                                    handleAllowCommentsToggle
                                }
                                handleChangeEmailRequired={
                                    handleChangeEmailRequired
                                }
                                handleDisplayProfilePictureToggle={
                                    handleDisplayProfilePictureToggle
                                }
                                isEmailRequiredChecked={isEmailRequiredChecked}
                                isSubChannel={isSubChannel}
                                register={register}
                            />
                            {!isSubChannel && (
                                <>
                                    <DemographicsSection
                                        control={control}
                                        demographicsToggled={
                                            demographicsToggled
                                        }
                                        handleDemographicsToggled={
                                            handleDemographicsToggled
                                        }
                                        isAdvancedDataCollectionLocked={
                                            isAdvancedDataCollectionLocked
                                        }
                                        register={register}
                                    />
                                    <CustomVariablesSection
                                        classes={classes}
                                        control={control}
                                        customVariableFields={
                                            customVariableFields
                                        }
                                        customVariablesToggled={
                                            customVariablesToggled
                                        }
                                        denormalizeOptions={denormalizeOptions}
                                        errors={errors}
                                        handleCustomVariablesToggle={
                                            handleCustomVariablesToggle
                                        }
                                        handleRemoveCustomVariable={
                                            handleRemoveCustomVariable
                                        }
                                        isAdvancedDataCollectionLocked={
                                            isAdvancedDataCollectionLocked
                                        }
                                        normalizeOptions={normalizeOptions}
                                        register={register}
                                        validateCustomVariableLabel={
                                            validateCustomVariableLabel
                                        }
                                        validateUniqueCustomVariableLabel={
                                            validateUniqueCustomVariableLabel
                                        }
                                        watchedCustomVariables={
                                            watchedCustomVariables
                                        }
                                    />
                                    <CustomTextFieldSection
                                        channelCustomTextFieldQuestion={
                                            channel.settings
                                                ?.custom_text_field_question
                                        }
                                        control={control}
                                        customTextFieldToggled={
                                            customTextFieldToggled
                                        }
                                        handleCustomTextFieldToggle={
                                            handleCustomTextFieldToggle
                                        }
                                        isAdvancedDataCollectionLocked={
                                            isAdvancedDataCollectionLocked
                                        }
                                        register={register}
                                        watchCustomTextFieldQuestion={
                                            watchCustomTextFieldQuestion
                                        }
                                    />
                                    <VotingRulesSection
                                        allowDoubleVoteChecked={
                                            allowDoubleVoteChecked
                                        }
                                        channelDoubleVoteCooldownMinutes={
                                            channel.settings
                                                ?.double_vote_cooldown_minutes
                                        }
                                        checked={checked}
                                        classes={classes}
                                        control={control}
                                        handleAllowDoubleVoteToggle={
                                            handleAllowDoubleVoteToggle
                                        }
                                        handleChange={handleChange}
                                        isAdvancedDataCollectionLocked={
                                            isAdvancedDataCollectionLocked
                                        }
                                        register={register}
                                    />
                                    <BrandingSection
                                        channelId={channel._id as string}
                                        channelUsesProfileAsQrLogo={
                                            channel?.settings
                                                ?.use_channel_profile_as_qr_logo
                                        }
                                        classes={classes}
                                        control={control}
                                        handleChangeQRLogo={handleChangeQRLogo}
                                        handleColorChange={handleColorChange}
                                        isBrandingLocked={isBrandingLocked}
                                        profileImgPath={profileImgPath}
                                        qrColor={qrColor as string}
                                        register={register}
                                        useChannelProfileAsQrLogoChecked={
                                            useChannelProfileAsQrLogoChecked
                                        }
                                    />
                                    <CompletionRedirectSection
                                        completionRedirectToggled={
                                            completionRedirectToggled
                                        }
                                        control={control}
                                        errors={errors}
                                        handleCompletionRedirectToggle={
                                            handleCompletionRedirectToggle
                                        }
                                        isBrandingLocked={isBrandingLocked}
                                        register={register}
                                    />
                                </>
                            )}
                        </Grid>
                    </form>
                </Box>
            </Box>
        </>
    );
};

const ChannelSettingsActions: React.FC<IChannel> = () => {
    const { t } = useTranslation();
    return (
        <>
            <TbPrimaryButton
                type="submit"
                form="channel-dialog"
                variant="contained"
                size="small"
            >
                {t("dialogs.save")}
            </TbPrimaryButton>
        </>
    );
};

// Provide dialog metadata consumed by the dialog orchestrator.
export default {
    content: ChannelSettingsContent,
    actions: ChannelSettingsActions,
    title: i18n.t("dialogs.channel_settings.dialog_title"),
};
